/**
 * ReportsListPage — Tela de lista de relatorios narrativos (/reports).
 *
 * Estados implementados (wireframe §1.6):
 *   loading             : skeleton de 3 cartoes
 *   empty-never-generated : nunca gerado — copy canonical com ETA
 *   empty-no-sources    : nenhuma fonte — CTA para /sources
 *   ready               : lista de cartoes com preview
 *   error               : erro ao buscar lista + retry
 *
 * Regras:
 *   - Zero string hardcoded (tudo via t())
 *   - Zero acesso direto a env vars
 *   - A11y AA: aria-labels, focus, keyboard nav, roles semanticos
 *   - CSS: screen-reports.css exclusivo
 *   - Botao "Gerar agora" conforme D-045 (geracao manual)
 *   - Codificado contra contrato de API do wireframe §HANDOFF; backend em paralelo
 */

import '../styles/screen-reports.css';

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { t } from '../lib/i18n';
import { PageHeading } from '../components/PageHeading';
import { Skeleton } from '../components/Skeleton';
import { apiClient } from '../lib/api-client';

/* ================================================================
   TIPOS — contrato de API (wireframe §HANDOFF — backend em paralelo)
   ================================================================ */

interface ReportSummary {
  readonly id: string;
  readonly type: 'weekly' | 'monthly';
  readonly period_label: string;
  readonly generated_at: string;
  readonly preview_sentence: string;
  readonly sources_used: readonly string[];
}

interface ReportsListResponse {
  readonly reports: readonly ReportSummary[];
}

type FilterType = 'all' | 'weekly' | 'monthly';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'empty-never-generated' }
  | { kind: 'empty-no-sources' }
  | { kind: 'ready'; data: readonly ReportSummary[] }
  | { kind: 'error' };

/* ================================================================
   HELPERS
   ================================================================ */

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

/** Determina se um relatorio e "novo" — gerado ha menos de 48h. */
function isNew(generatedAt: string): boolean {
  try {
    const diff = Date.now() - new Date(generatedAt).getTime();
    return diff < 48 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

/* ================================================================
   COMPONENTES INTERNOS
   ================================================================ */

/** Skeleton de cartao de relatorio (proporcoes do wireframe §1.6). */
function ReportCardSkeleton(): JSX.Element {
  return (
    <li className="reports-skeleton-card" aria-hidden="true">
      {/* chip 24px */}
      <Skeleton variant="text" width="80px" />
      {/* titulo 20px */}
      <Skeleton variant="heading" width="60%" />
      {/* preview 14px */}
      <Skeleton variant="text" width="90%" />
      <Skeleton variant="text" width="70%" />
    </li>
  );
}

interface ReportCardProps {
  readonly report: ReportSummary;
}

function ReportCard({ report }: ReportCardProps): JSX.Element {
  const typeLabel = report.type === 'weekly' ? t('report.badgeWeekly') : t('report.badgeMonthly');
  const generatedDate = formatDate(report.generated_at);
  const newBadge = isNew(report.generated_at);

  return (
    <li>
      <article className="report-card">
        <div className="report-card-header">
          {newBadge ? (
            <span className="report-card-badge-new" aria-label={t('report.listNew')}>
              {t('report.listNew')}
            </span>
          ) : null}
          <h2 className="report-card-title">{report.period_label}</h2>
        </div>

        <div className="report-card-meta">
          <span className="report-card-type-chip">{typeLabel}</span>
          <span aria-label={t('report.generatedAt', { data: generatedDate })}>
            {t('report.generatedAt', { data: generatedDate })}
          </span>
        </div>

        {report.preview_sentence.length > 0 ? (
          <p className="report-card-preview">&ldquo;{report.preview_sentence}&rdquo;</p>
        ) : null}

        <Link
          to={`/reports/${report.id}`}
          className="report-card-cta"
          aria-label={`${t('report.listCardViewCta')} — ${report.period_label}`}
        >
          {t('report.listCardViewCta')}
          <span className="material-symbols-rounded" aria-hidden="true" style={{ fontSize: 16 }}>
            arrow_forward
          </span>
        </Link>
      </article>
    </li>
  );
}

/* ================================================================
   PAGINA PRINCIPAL
   ================================================================ */

export function ReportsListPage(): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [filter, setFilter] = useState<FilterType>('all');

  const load = useCallback(async (): Promise<void> => {
    setState({ kind: 'loading' });
    try {
      const data = await apiClient.get<ReportsListResponse>('/reports');
      const reports = Array.isArray(data?.reports) ? data.reports : [];

      if (reports.length === 0) {
        // Distingue "nunca gerado" de "sem fontes": a API retorna reason no caso de sem fontes
        // Fallback: trata como empty-never-generated (estado mais informativo)
        setState({ kind: 'empty-never-generated' });
      } else {
        setState({ kind: 'ready', data: reports });
      }
    } catch (err: unknown) {
      // Se o erro indica "no sources connected", usa o estado especifico
      if (err instanceof Error && err.message.includes('no_sources')) {
        setState({ kind: 'empty-no-sources' });
      } else {
        setState({ kind: 'error' });
      }
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Filtro local de tipo
  const filteredReports =
    state.kind === 'ready' ? state.data.filter((r) => filter === 'all' || r.type === filter) : [];

  return (
    <section className="reports-page" aria-labelledby="reports-page-heading">
      <PageHeading
        id="reports-page-heading"
        title={t('report.listTitle')}
        subtitle={t('report.listSubtitle')}
      />

      {/* Filtros — exibidos apenas quando ha dados */}
      {state.kind === 'ready' ? (
        <div className="reports-filters" role="group" aria-label="Filtrar por tipo de relatorio">
          {(['all', 'weekly', 'monthly'] as const).map((f) => {
            const labels: Record<FilterType, string> = {
              all: t('report.listFilterAll'),
              weekly: t('report.listFilterWeekly'),
              monthly: t('report.listFilterMonthly'),
            };
            return (
              <button
                key={f}
                type="button"
                className={`reports-filter-btn${filter === f ? ' active' : ''}`}
                onClick={() => setFilter(f)}
                aria-pressed={filter === f}
              >
                {labels[f]}
              </button>
            );
          })}
        </div>
      ) : null}

      {/* Estado: loading */}
      {state.kind === 'loading' ? (
        <ul
          className="reports-skeleton-list"
          role="status"
          aria-label={t('report.generatingAria')}
          aria-busy="true"
        >
          <ReportCardSkeleton />
          <ReportCardSkeleton />
          <ReportCardSkeleton />
        </ul>
      ) : null}

      {/* Estado: empty-never-generated */}
      {state.kind === 'empty-never-generated' ? (
        <div className="reports-empty" role="region" aria-label="Sem relatorios ainda">
          <span className="material-symbols-rounded reports-empty-icon" aria-hidden="true">
            summarize
          </span>
          <h2 className="reports-empty-title">{t('report.emptyFirstTimeTitle')}</h2>
          <p className="reports-empty-body">{t('report.emptyFirstTimeBody')}</p>
          <div className="reports-empty-actions">
            <Link to="/dashboard" className="report-btn-primary">
              {t('report.emptyFirstTimeCta')}
            </Link>
            <Link to="/radar" className="report-btn-secondary">
              {t('report.emptyFirstTimeCta2')}
            </Link>
          </div>
        </div>
      ) : null}

      {/* Estado: empty-no-sources */}
      {state.kind === 'empty-no-sources' ? (
        <div className="reports-empty" role="region" aria-label="Sem fontes conectadas">
          <span className="material-symbols-rounded reports-empty-icon" aria-hidden="true">
            hub
          </span>
          <h2 className="reports-empty-title">{t('report.emptyNoSourceTitle')}</h2>
          <p className="reports-empty-body">{t('report.emptyNoSourceBody')}</p>
          <div className="reports-empty-actions">
            <Link to="/sources" className="report-btn-primary">
              {t('report.emptyNoSourceCta')}
            </Link>
          </div>
        </div>
      ) : null}

      {/* Estado: ready */}
      {state.kind === 'ready' ? (
        filteredReports.length === 0 ? (
          <p
            className="reports-empty-body"
            style={{ textAlign: 'center', padding: 'var(--mkv-space-8) 0' }}
          >
            {t('report.historyEmpty')}
          </p>
        ) : (
          <ul className="reports-list" aria-label={t('report.historyLabel')}>
            {filteredReports.map((report) => (
              <ReportCard key={report.id} report={report} />
            ))}
          </ul>
        )
      ) : null}

      {/* Estado: error */}
      {state.kind === 'error' ? (
        <div className="reports-error" role="alert">
          <span
            className="material-symbols-rounded"
            aria-hidden="true"
            style={{ fontSize: 40, color: 'var(--md-error)' }}
          >
            error_outline
          </span>
          <h2 className="reports-error-title">{t('report.errorListTitle')}</h2>
          <p className="reports-error-body">{t('report.errorListBody')}</p>
          <div className="reports-error-actions">
            <button type="button" className="report-btn-primary" onClick={() => void load()}>
              {t('report.errorCta')}
            </button>
            <a href="mailto:suporte@mktvibe.com.br" className="report-btn-secondary">
              {t('report.errorCta2')}
            </a>
          </div>
        </div>
      ) : null}
    </section>
  );
}
